import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { collection, getDocs } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { ROLES, roleRank, roleLabel, isCreator, canManageAccount } from "../lib/roles";
import { packageLabel } from "../lib/packages";
import Modal from "../components/Modal";
import { SideNav, MobileBar, useCollapsed, useWide } from "../components/AppNav";

// Beheerpagina (build 2.4), alleen voor de creator: overzicht, aanmeldingen, gebruikers,
// clubs. Alle wijzigingen lopen via de bestaande Cloud Functions. Licenties (pakket,
// geldig t/m, factuurstatus) komen pas met het licentiemodel; tot die tijd tonen we het
// pakket dat bij de aanmelding is gekozen.

var fnCreate = httpsCallable(functions, "createAccount");
var fnRole = httpsCallable(functions, "updateRole");
var fnName = httpsCallable(functions, "updateName");
var fnLink = httpsCallable(functions, "passwordLink");
var fnDelete = httpsCallable(functions, "deleteAccount");
var fnApprove = httpsCallable(functions, "approveClub");
var fnReject = httpsCallable(functions, "rejectClub");

var CSS = `
.bh{font-family:'Archivo',system-ui,sans-serif;color:#1C1B19;background:#F5F3EE;min-height:100vh;display:flex;align-items:flex-start}
.bh-main{flex:1;min-width:0;padding:32px 36px}
.bh-card{background:#fff;border:1px solid #E6E2D9;border-radius:14px}
.bh-h1{font-size:30px;font-weight:800;letter-spacing:-0.4px;margin-bottom:4px}
.bh-sub{font-size:13px;color:#6B675F}
.bh-pill{display:inline-flex;align-items:center;gap:6px;padding:3px 10px;border-radius:999px;font-size:12px;font-weight:700;white-space:nowrap}
.bh-dot{width:7px;height:7px;border-radius:50%}
.bh-btn{display:inline-flex;align-items:center;justify-content:center;gap:8px;min-height:42px;padding:0 16px;border-radius:10px;background:#1A2744;color:#fff;border:none;font-weight:700;font-size:14px;cursor:pointer}
.bh-btn:disabled{opacity:.6;cursor:default}
.bh-btn2{display:inline-flex;align-items:center;justify-content:center;gap:8px;min-height:42px;padding:0 16px;border-radius:10px;background:#fff;color:#1C1B19;border:1px solid #D6D1C6;font-weight:700;font-size:14px;cursor:pointer}
.bh-btnr{display:inline-flex;align-items:center;justify-content:center;min-height:40px;padding:0 14px;border-radius:9px;border:1px solid #E2B7B4;background:#fff;color:#B83232;font-weight:700;font-size:13px;cursor:pointer}
.bh-btng{display:inline-flex;align-items:center;justify-content:center;min-height:40px;padding:0 14px;border-radius:9px;border:none;background:#2F7D47;color:#fff;font-weight:700;font-size:13px;cursor:pointer}
.bh-btnr:disabled,.bh-btng:disabled{opacity:.5;cursor:default}
.bh-chip{display:inline-flex;align-items:center;min-height:36px;padding:0 14px;border-radius:999px;border:1px solid #D6D1C6;background:#fff;font-size:13px;font-weight:700;color:#1C1B19;cursor:pointer}
.bh-chip.on{background:#1A2744;border-color:#1A2744;color:#fff}
.bh-inp{width:100%;min-height:42px;padding:0 12px;border-radius:9px;border:1px solid #CFCAC0;font-size:14px;background:#fff;outline:none;color:#1C1B19}
.bh-inp:focus{border-color:#1A2744}
.bh-lbl{display:block;font-size:12px;font-weight:700;color:#6B675F;margin-bottom:5px}
.bh-sec{font-size:12px;font-weight:800;letter-spacing:.5px;color:#6B675F;text-transform:uppercase}
.bh-table{width:100%;border-collapse:collapse}
.bh-table th{text-align:left;font-size:12px;font-weight:700;color:#6B675F;letter-spacing:.5px;text-transform:uppercase;padding:12px 16px;border-bottom:1px solid #E2DED5;white-space:nowrap}
.bh-table td{padding:13px 16px;border-bottom:1px solid #EEEBE4;font-size:14px;vertical-align:middle;white-space:nowrap}
.bh-table tbody tr{cursor:pointer}
.bh-table tbody tr:hover{background:#FAF8F4}
.bh-table tbody tr.sel{background:#EAF0F7;box-shadow:inset 3px 0 0 #1A2744}
.bh-table tbody tr:last-child td{border-bottom:none}
.bh-row{display:flex;gap:16px;align-items:center;flex-wrap:wrap;padding:16px 20px;border-bottom:1px solid #E2DED5}
.bh-row:last-child{border-bottom:none}
.bh-act{display:flex;align-items:center;gap:10px;width:100%;min-height:44px;padding:0 14px;border-radius:10px;border:1px solid #E2DED5;background:#fff;font-size:14px;font-weight:600;color:#1C1B19;text-align:left;cursor:pointer}
.bh-act:disabled{opacity:.5;cursor:default}
@media (max-width:899px){
  .bh-main{padding:20px 16px}
  .bh-h1{font-size:24px}
}
`;

function Icon({ d, size }) {
  return (
    <svg width={size || 18} height={size || 18} viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flex: "none" }}>
      {d.map(function (x, i) { return <path key={i} d={x} />; })}
    </svg>
  );
}
var IC = {
  overzicht: ["M3 3h7v7H3z", "M14 3h7v7h-7z", "M3 14h7v7H3z", "M14 14h7v7h-7z"],
  aanmeldingen: ["M4 4h16v12H8l-4 4z"],
  gebruikers: ["M9 11.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z", "M2.5 20c.8-3.5 3.4-5.5 6.5-5.5s5.7 2 6.5 5.5", "M16 4.5a3.5 3.5 0 0 1 0 7", "M18 14.8c1.9.7 3.1 2.5 3.5 5.2"],
  clubs: ["M3 21V9l9-6 9 6v12", "M9 21v-7h6v7"],
  licenties: ["M3 5h18v14H3z", "M3 10h18", "M7 15h4"],
  terug: ["M15 6l-6 6 6 6"],
  plus: ["M12 5v14", "M5 12h14"],
  zoek: ["M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14z", "M16.5 16.5L21 21"],
  sluit: ["M6 6l12 12", "M18 6L6 18"],
  rol: ["M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8z", "M4 21c1-4 4-6 8-6s7 2 8 6"],
  mail: ["M3 5h18v14H3z", "M3 7l9 6 9-6"],
  prullenbak: ["M4 7h16", "M9 7V4h6v3", "M6 7l1 13h10l1-13"],
  pen: ["M4 20h4L19 9l-4-4L4 16z"],
  open: ["M14 4h6v6", "M20 4l-9 9", "M18 14v6H4V6h6"],
};

var VIEWS = [
  { key: "overzicht", label: "Overzicht" },
  { key: "aanmeldingen", label: "Aanmeldingen" },
  { key: "gebruikers", label: "Gebruikers" },
  { key: "clubs", label: "Clubs" },
  { key: "licenties", label: "Licenties & facturen" },
];

var AV_COLORS = [["#EAF0F7", "#2A4E78"], ["#FDF1E7", "#B85A12"], ["#EDF6EF", "#2F7D47"], ["#FBEDF4", "#B83A7A"], ["#F2EEFA", "#6A4BB0"], ["#E8F5F6", "#1D7F8C"]];
function Avatar({ name, size }) {
  var n = String(name || "?").trim();
  var parts = n.split(/\s+/);
  var ini = (parts[0].charAt(0) + (parts.length > 1 ? parts[parts.length - 1].charAt(0) : "")).toUpperCase();
  var h = 0; for (var i = 0; i < n.length; i++) h = (h * 31 + n.charCodeAt(i)) % 997;
  var c = AV_COLORS[h % AV_COLORS.length];
  var s = size || 40;
  return <div style={{ width: s, height: s, borderRadius: "50%", background: c[0], color: c[1], display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: s * 0.36, flex: "none" }}>{ini}</div>;
}

function Pill({ tone, children, dot }) {
  var t = {
    green: ["#EDF6EF", "#22603A", "#2F7D47"], orange: ["#FDF1E7", "#8F4510", "#B85A12"], yellow: ["#FCF6DC", "#6B5000", "#C9A21C"],
    red: ["#FBEDEC", "#9A2A2A", "#B83232"], blue: ["#EAF0F7", "#2A4E78", "#2A4E78"], grey: ["#F0F1F3", "#1C1B19", "#6B675F"],
  }[tone || "grey"];
  return <span className="bh-pill" style={{ background: t[0], color: t[1] }}>{dot && <span className="bh-dot" style={{ background: t[2] }} />}{children}</span>;
}

function errText(e) {
  if (e && e.message && e.code && String(e.code).indexOf("functions/") === 0 && e.code !== "functions/internal") return e.message;
  return "Er ging iets mis (" + ((e && (e.code || e.message)) || "onbekend") + ").";
}

function slugify(v) {
  return String(v || "").toLowerCase()
    .normalize("NFD").replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 40);
}

function ago(ts) {
  if (!ts || !ts.toDate) return "";
  var days = Math.floor((Date.now() - ts.toDate().getTime()) / 86400000);
  if (days <= 0) return "vandaag";
  if (days === 1) return "gisteren";
  return days + " dagen geleden";
}

function LinkBox({ link }) {
  var [copied, setCopied] = useState(false);
  function copy() {
    if (navigator.clipboard) navigator.clipboard.writeText(link).then(function () { setCopied(true); setTimeout(function () { setCopied(false); }, 1500); });
  }
  var wa = "https://wa.me/?text=" + encodeURIComponent("Je account voor Seizoensplan staat klaar. Stel via deze link je wachtwoord in (link is 1 uur geldig): " + link);
  return (
    <div>
      <textarea readOnly value={link} rows={3} onFocus={function (e) { e.target.select(); }} className="bh-inp" style={{ fontSize: 11, color: "#555", resize: "none", padding: 10, marginBottom: 10 }} />
      <div style={{ display: "flex", gap: 8 }}>
        <button type="button" className="bh-btn" onClick={copy} style={{ flex: 1 }}>{copied ? "Gekopieerd ✓" : "Link kopiëren"}</button>
        <a href={wa} target="_blank" rel="noreferrer" className="bh-btn" style={{ flex: 1, background: "#25D366", textDecoration: "none" }}>WhatsApp</a>
      </div>
    </div>
  );
}

// ---- Aanmelding (kaart met aanpasbare clubnaam en webadres) -------------------------
function RequestRow({ req, busy, onApprove, onReject, compact }) {
  var [name, setName] = useState(req.clubName || req.name || "");
  var [slug, setSlug] = useState(slugify(req.clubName || req.name));
  var [touched, setTouched] = useState(false);
  var trainer = req.type === "trainer";
  return (
    <div className="bh-row" style={{ opacity: busy ? 0.6 : 1, alignItems: compact ? "center" : "flex-start" }}>
      <Avatar name={trainer ? req.name : req.clubName} />
      <div style={{ flex: "1 1 260px", minWidth: 0 }}>
        <div style={{ fontWeight: 700, fontSize: 15 }}>
          {trainer ? req.name : req.clubName}{" "}
          <span style={{ fontWeight: 500, color: "#6B675F", fontSize: 13 }}>· {trainer ? "losse trainer" : "club"}{req.place ? ", " + req.place : ""}</span>
        </div>
        <div className="bh-sub">{trainer ? "" : req.name + " · "}<a href={"mailto:" + req.email} style={{ color: "#6B675F" }}>{req.email}</a>{req.createdAt ? " · " + ago(req.createdAt) : ""}</div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
          {req.pakket ? <Pill>{packageLabel(req.pakket)}</Pill> : <Pill tone="yellow">Geen pakket gekozen</Pill>}
        </div>
        {!compact && (
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 12 }}>
            <label style={{ flex: "1 1 180px" }}><span className="bh-lbl">{trainer ? "Naam omgeving" : "Clubnaam"}</span>
              <input className="bh-inp" value={name} onChange={function (e) { setName(e.target.value); if (!touched) setSlug(slugify(e.target.value)); }} />
            </label>
            <label style={{ flex: "1 1 180px" }}><span className="bh-lbl">Webadres</span>
              <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <span className="bh-sub" style={{ whiteSpace: "nowrap" }}>…web.app/</span>
                <input className="bh-inp" value={slug} onChange={function (e) { setTouched(true); setSlug(slugify(e.target.value)); }} />
              </span>
            </label>
          </div>
        )}
      </div>
      <div style={{ display: "flex", gap: 8, alignSelf: compact ? "center" : "flex-end" }}>
        <button type="button" className="bh-btnr" disabled={busy} onClick={function () { onReject(req); }}>Afwijzen</button>
        <button type="button" className="bh-btng" disabled={busy || slug.length < 2 || name.trim().length < 2} onClick={function () { onApprove(req, name.trim(), slug); }}>Goedkeuren</button>
      </div>
    </div>
  );
}

// ---- Zijpaneel bij een gebruiker ---------------------------------------------------
function UserPanel({ u, me, clubName, pakket, busy, onClose, onRole, onName, onLink, onDelete }) {
  var [editing, setEditing] = useState(false);
  var [nm, setNm] = useState(u.name || "");
  useEffect(function () { setEditing(false); setNm(u.name || ""); }, [u.uid, u.name]);
  var manage = canManageAccount(me, u);
  return (
    <aside className="bh-card" aria-label={"Details van " + (u.name || u.email)} style={{ flex: "1 1 320px", maxWidth: 400, minWidth: 0, padding: 22, boxShadow: "0 16px 40px rgba(28,27,25,0.10)", display: "flex", flexDirection: "column", gap: 18, alignSelf: "flex-start" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
        <div style={{ display: "flex", gap: 12, alignItems: "center", minWidth: 0 }}>
          <Avatar name={u.name || u.email} size={44} />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 18, fontWeight: 800 }}>{u.name || "(geen naam)"}</div>
            <div className="bh-sub" style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{u.email}</div>
          </div>
        </div>
        <button type="button" aria-label="Paneel sluiten" onClick={onClose} style={{ width: 36, height: 36, borderRadius: 8, border: "1px solid #E2DED5", background: "#fff", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#1C1B19" }}><Icon d={IC.sluit} size={14} /></button>
      </div>

      <div style={{ background: "#FAF8F4", borderRadius: 12, padding: 16, display: "grid", gridTemplateColumns: "100px minmax(0,1fr)", rowGap: 8, fontSize: 14 }}>
        <span className="bh-sub" style={{ fontSize: 14 }}>Club</span><span style={{ fontWeight: 700 }}>{clubName || "–"}</span>
        <span className="bh-sub" style={{ fontSize: 14 }}>Rol</span><span>{roleLabel(u.role)}</span>
        <span className="bh-sub" style={{ fontSize: 14 }}>Pakket</span><span>{pakket ? packageLabel(pakket) : "–"}</span>
        <span className="bh-sub" style={{ fontSize: 14 }}>Licentie</span><span className="bh-sub" style={{ fontSize: 14 }}>Volgt met het licentiemodel</span>
      </div>

      {!manage ? (
        <p className="bh-sub" style={{ fontSize: 14 }}>{u.uid === me.uid ? "Dit ben jij. Je eigen account beheer je via het accountmenu in de app." : "Dit account kun je hier niet beheren."}</p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <span className="bh-sec">Account</span>
          <label style={{ display: "flex", alignItems: "center", gap: 10, minHeight: 44, padding: "0 6px 0 14px", borderRadius: 10, border: "1px solid #E2DED5", fontSize: 14, fontWeight: 600 }}>
            <Icon d={IC.rol} size={16} />Rol
            <select value={u.role} disabled={busy} onChange={function (e) { onRole(u, e.target.value); }} style={{ marginLeft: "auto", border: "none", background: "transparent", fontSize: 14, fontWeight: 700, color: "#1C1B19", padding: "8px 6px", cursor: "pointer" }}>
              {ROLES.map(function (r) { return <option key={r.key} value={r.key}>{r.label}</option>; })}
            </select>
          </label>
          {editing ? (
            <div style={{ display: "flex", gap: 8 }}>
              <input className="bh-inp" value={nm} autoFocus onChange={function (e) { setNm(e.target.value); }} aria-label="Naam" />
              <button type="button" className="bh-btn" disabled={busy || nm.trim().length < 2} onClick={function () { onName(u, nm.trim()); }}>Opslaan</button>
            </div>
          ) : (
            <button type="button" className="bh-act" disabled={busy} onClick={function () { setEditing(true); }}><Icon d={IC.pen} size={16} />Naam aanpassen</button>
          )}
          <button type="button" className="bh-act" disabled={busy} onClick={function () { onLink(u); }}><Icon d={IC.mail} size={16} />Wachtwoordlink sturen</button>
          <button type="button" className="bh-act" disabled={busy} onClick={function () { onDelete(u); }} style={{ color: "#9A2A2A", borderColor: "#EBC9C6" }}><Icon d={IC.prullenbak} size={16} />Account verwijderen</button>
        </div>
      )}
    </aside>
  );
}

// ---- Hoofdcomponent ---------------------------------------------------------------
export default function Beheer() {
  var { user, profile, loading, resetPassword } = useAuth();
  var navigate = useNavigate();
  var [view, setView] = useState("overzicht");
  var [users, setUsers] = useState(null);
  var [clubs, setClubs] = useState([]);
  var [requests, setRequests] = useState([]); // alle clubRequests
  var [loadErr, setLoadErr] = useState("");
  var [busy, setBusy] = useState(null);
  var [err, setErr] = useState("");
  var [mdl, setMdl] = useState(null);
  var [sel, setSel] = useState(null);
  var [q, setQ] = useState("");
  var [roleF, setRoleF] = useState("");
  var [clubF, setClubF] = useState("");
  var panelRef = useRef(null);
  var wide = useWide();
  var [collapsed, toggleCollapsed] = useCollapsed();
  var creator = !!profile && isCreator(profile);

  var load = useCallback(async function () {
    setLoadErr("");
    try {
      var res = await Promise.all([getDocs(collection(db, "clubs")), getDocs(collection(db, "users")), getDocs(collection(db, "clubRequests"))]);
      setClubs(res[0].docs.map(function (d) { return Object.assign({ id: d.id }, d.data()); }));
      var ul = res[1].docs.map(function (d) { return Object.assign({ uid: d.id }, d.data()); }).filter(function (u) { return u.status !== "pending"; });
      ul.sort(function (a, b) { return roleRank(b.role) - roleRank(a.role) || (a.name || a.email || "").localeCompare(b.name || b.email || ""); });
      setUsers(ul);
      setRequests(res[2].docs.map(function (d) { return d.data(); }));
    } catch (e) {
      console.error(e);
      setLoadErr("Gegevens laden mislukt (" + (e.code || e.message) + ").");
      setUsers([]);
    }
  }, []);
  useEffect(function () { if (creator) load(); }, [creator, load]);

  var clubMap = useMemo(function () { var m = {}; clubs.forEach(function (c) { m[c.id] = c; }); return m; }, [clubs]);
  var pending = useMemo(function () {
    return requests.filter(function (r) { return r.status === "pending"; })
      .sort(function (a, b) { return ((a.createdAt && a.createdAt.seconds) || 0) - ((b.createdAt && b.createdAt.seconds) || 0); });
  }, [requests]);
  // Pakket per gebruiker: uit zijn eigen aanmelding, anders dat van zijn club (de
  // aanmelding van de hoofdtrainer). Atalanta en handmatig aangemaakte clubs hebben er geen.
  var pakketOf = useMemo(function () {
    var byUid = {}, byClub = {};
    requests.forEach(function (r) {
      if (!r.pakket) return;
      byUid[r.uid] = r.pakket;
      if (r.status === "approved" && r.clubId) byClub[r.clubId] = r.pakket;
    });
    var m = {};
    (users || []).forEach(function (u) { var p = byUid[u.uid] || byClub[u.clubId]; if (p) m[u.uid] = p; });
    return m;
  }, [requests, users]);
  var trainerClubs = useMemo(function () { var s = {}; requests.forEach(function (r) { if (r.status === "approved" && r.type === "trainer" && r.clubId) s[r.clubId] = true; }); return s; }, [requests]);

  if (loading) return null;
  if (!user) return <Navigate to="/login" replace state={{ from: "/beheer" }} />;
  if (!profile) return null;
  if (!creator) return <Navigate to="/" replace />;

  function clubName(id) { var c = clubMap[id]; return c ? (c.name || c.slug || id) : (id || ""); }

  // ---- acties ----
  async function run(key, fn) {
    setErr(""); setBusy(key);
    try { await fn(); } catch (e) { console.error(e); setErr(errText(e)); }
    setBusy(null);
  }
  function approve(req, name, slug) { return run(req.uid, async function () { await fnApprove({ uid: req.uid, clubName: name, slug: slug }); await load(); }); }
  function reject(req) { setMdl(null); return run(req.uid, async function () { await fnReject({ uid: req.uid }); await load(); }); }
  function changeRole(u, role) { if (role === u.role) return; return run(u.uid, async function () { await fnRole({ uid: u.uid, role: role }); await load(); }); }
  function saveName(u, name) { return run(u.uid, async function () { await fnName({ uid: u.uid, name: name }); await load(); }); }
  function newLink(u) {
    return run(u.uid, async function () {
      var res = await fnLink({ uid: u.uid });
      var mailSent = true;
      try { await resetPassword(u.email); } catch (e2) { console.error(e2); mailSent = false; }
      setMdl({ type: "link", user: u, link: res.data.link, mailSent: mailSent });
    });
  }
  function doDelete(u) { setMdl(null); return run(u.uid, async function () { await fnDelete({ uid: u.uid }); setSel(null); await load(); }); }

  function pick(u) {
    setSel(u.uid);
    setTimeout(function () { if (panelRef.current && window.innerWidth < 860) panelRef.current.scrollIntoView({ behavior: "smooth", block: "start" }); }, 50);
  }

  // ---- views ----
  function Header({ title, sub, action }) {
    return (
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 16, flexWrap: "wrap", marginBottom: 24 }}>
        <div><h1 className="bh-h1">{title}</h1>{sub && <p style={{ color: "#6B675F", fontSize: 15 }}>{sub}</p>}</div>
        {action}
      </div>
    );
  }

  var newAccountBtn = <button type="button" className="bh-btn" onClick={function () { setMdl({ type: "create" }); }}><Icon d={IC.plus} size={16} />Account aanmaken</button>;

  function renderOverzicht() {
    var oldest = pending[0] && pending[0].createdAt ? ago(pending[0].createdAt) : "";
    var counts = clubs.map(function (c) { return { c: c, n: (users || []).filter(function (u) { return u.clubId === c.id; }).length }; })
      .sort(function (a, b) { return b.n - a.n; });
    var max = Math.max(1, ...counts.map(function (x) { return x.n; }));
    var nTrainerClubs = clubs.filter(function (c) { return trainerClubs[c.id]; }).length;
    var tile = function (label, value, sub, subColor, extra) {
      return (
        <div className="bh-card" style={Object.assign({ padding: 20 }, extra || {})}>
          <div className="bh-sub" style={{ fontWeight: 600, marginBottom: 8 }}>{label}</div>
          <div style={{ fontSize: 34, fontWeight: 800 }}>{value}</div>
          {sub && <div style={{ fontSize: 13, color: subColor || "#6B675F", fontWeight: 600, marginTop: 4 }}>{sub}</div>}
        </div>
      );
    };
    return (
      <>
        <Header title="Overzicht" action={newAccountBtn} />
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 16, marginBottom: 24 }}>
          {tile("Actieve gebruikers", users ? users.length : "…")}
          {tile("Wachten op goedkeuring", pending.length, oldest ? "Oudste: " + oldest : "Niets open", pending.length ? "#7A5C00" : null, pending.length ? { borderColor: "#F1DC85", background: "#FFFCF0" } : null)}
          {tile("Clubs", clubs.length - nTrainerClubs, nTrainerClubs ? "en " + nTrainerClubs + (nTrainerClubs === 1 ? " losse trainer" : " losse trainers") : null)}
          {tile("Licenties", "–", "Volgt met het licentiemodel")}
        </div>
        <div style={{ display: "flex", gap: 24, flexWrap: "wrap", alignItems: "flex-start" }}>
          <section className="bh-card" style={{ flex: "3 1 440px", minWidth: 0, overflow: "hidden" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "18px 20px", borderBottom: "1px solid #E2DED5" }}>
              <h2 style={{ fontSize: 17, fontWeight: 800 }}>Nieuwe aanmeldingen</h2>
              {pending.length > 0 && <button type="button" onClick={function () { setView("aanmeldingen"); }} style={{ background: "none", border: "none", color: "#1A2744", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>Alles bekijken</button>}
            </div>
            {pending.length === 0 ? <p className="bh-sub" style={{ padding: 20, fontSize: 14 }}>Er zijn geen openstaande aanmeldingen.</p>
              : pending.slice(0, 3).map(function (r) { return <RequestRow key={r.uid} req={r} compact busy={busy === r.uid} onApprove={approve} onReject={function (x) { setMdl({ type: "reject", req: x }); }} />; })}
          </section>
          <section className="bh-card" style={{ flex: "2 1 300px", minWidth: 0, overflow: "hidden" }}>
            <div style={{ padding: "18px 20px", borderBottom: "1px solid #E2DED5" }}><h2 style={{ fontSize: 17, fontWeight: 800 }}>Te doen</h2></div>
            <div style={{ padding: "8px 20px 14px" }}>
              {pending.length > 0 ? (
                <div style={{ display: "flex", gap: 12, alignItems: "flex-start", padding: "12px 0" }}>
                  <Pill tone="yellow" dot>Open</Pill>
                  <div><div style={{ fontSize: 14, fontWeight: 700 }}>{pending.length === 1 ? "1 aanmelding wacht" : pending.length + " aanmeldingen wachten"} op goedkeuring</div><div className="bh-sub">Keur goed en stuur een factuur</div></div>
                </div>
              ) : <p className="bh-sub" style={{ padding: "12px 0", fontSize: 14 }}>Niets te doen.</p>}
            </div>
          </section>
        </div>
        <section className="bh-card" style={{ marginTop: 24, overflow: "hidden" }}>
          <div style={{ padding: "18px 20px", borderBottom: "1px solid #E2DED5" }}><h2 style={{ fontSize: 17, fontWeight: 800 }}>Gebruikers per club</h2></div>
          <div style={{ padding: "20px", display: "flex", flexDirection: "column", gap: 14 }}>
            {counts.length === 0 && <p className="bh-sub">Nog geen clubs.</p>}
            {counts.map(function (x) {
              return (
                <div key={x.c.id} style={{ display: "grid", gridTemplateColumns: "minmax(90px,160px) minmax(0,1fr) 40px", gap: 12, alignItems: "center", fontSize: 14 }}>
                  <span style={{ fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{x.c.name || x.c.slug}</span>
                  <div style={{ height: 12, borderRadius: 6, background: "#EEEBE4" }}><div style={{ width: (x.n / max * 100) + "%", height: 12, borderRadius: 6, background: "#1A2744" }} /></div>
                  <span style={{ textAlign: "right", fontWeight: 700 }}>{x.n}</span>
                </div>
              );
            })}
          </div>
        </section>
      </>
    );
  }

  function renderAanmeldingen() {
    var done = requests.filter(function (r) { return r.status !== "pending"; })
      .sort(function (a, b) { return ((b.decidedAt && b.decidedAt.seconds) || 0) - ((a.decidedAt && a.decidedAt.seconds) || 0); }).slice(0, 10);
    return (
      <>
        <Header title="Aanmeldingen" sub={pending.length ? pending.length + " open" : "Geen open aanmeldingen"} />
        <section className="bh-card" style={{ overflow: "hidden", marginBottom: 24 }}>
          {pending.length === 0 ? <p className="bh-sub" style={{ padding: 20, fontSize: 14 }}>Er zijn geen openstaande aanmeldingen.</p>
            : pending.map(function (r) { return <RequestRow key={r.uid} req={r} busy={busy === r.uid} onApprove={approve} onReject={function (x) { setMdl({ type: "reject", req: x }); }} />; })}
        </section>
        {done.length > 0 && <>
          <h2 style={{ fontSize: 17, fontWeight: 800, marginBottom: 12 }}>Eerder beoordeeld</h2>
          <section className="bh-card" style={{ overflowX: "auto" }}>
            <table className="bh-table">
              <thead><tr><th>Naam</th><th>Soort</th><th>Pakket</th><th>Besluit</th></tr></thead>
              <tbody>
                {done.map(function (r) {
                  return (
                    <tr key={r.uid} style={{ cursor: "default" }}>
                      <td><div style={{ fontWeight: 700 }}>{r.type === "trainer" ? r.name : r.clubName}</div><div className="bh-sub">{r.email}</div></td>
                      <td>{r.type === "trainer" ? "Losse trainer" : "Club"}</td>
                      <td>{r.pakket ? packageLabel(r.pakket) : "–"}</td>
                      <td>{r.status === "approved" ? <Pill tone="green" dot>Goedgekeurd</Pill> : <Pill tone="red" dot>Afgewezen</Pill>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </section>
        </>}
      </>
    );
  }

  function renderGebruikers() {
    var list = (users || []).filter(function (u) {
      if (roleF && u.role !== roleF && !(roleF === "creator" && u.role === "owner")) return false;
      if (clubF && u.clubId !== clubF) return false;
      if (q) {
        var t = q.toLowerCase();
        return (u.name || "").toLowerCase().indexOf(t) >= 0 || (u.email || "").toLowerCase().indexOf(t) >= 0 || clubName(u.clubId).toLowerCase().indexOf(t) >= 0;
      }
      return true;
    });
    var selU = sel && (users || []).find(function (u) { return u.uid === sel; });
    var roleCount = function (k) { return (users || []).filter(function (u) { return u.role === k || (k === "creator" && u.role === "owner"); }).length; };
    return (
      <>
        <Header title="Gebruikers" sub={users ? users.length + " accounts bij " + clubs.length + " clubs" : "Laden…"} action={newAccountBtn} />
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginBottom: 16 }}>
          <label style={{ flex: "1 1 240px", maxWidth: 360, display: "flex", alignItems: "center", gap: 8, minHeight: 42, padding: "0 12px", borderRadius: 10, border: "1px solid #CFCAC0", background: "#fff", color: "#6B675F" }}>
            <Icon d={IC.zoek} size={16} />
            <input type="search" value={q} onChange={function (e) { setQ(e.target.value); }} placeholder="Zoek op naam, e-mail of club" aria-label="Zoeken" style={{ flex: 1, border: "none", outline: "none", fontSize: 14, background: "transparent", minWidth: 0, color: "#1C1B19" }} />
          </label>
          <button type="button" className={"bh-chip" + (roleF === "" ? " on" : "")} onClick={function () { setRoleF(""); }}>Alle {(users || []).length}</button>
          {ROLES.map(function (r) {
            var n = roleCount(r.key);
            if (!n) return null;
            return <button key={r.key} type="button" className={"bh-chip" + (roleF === r.key ? " on" : "")} onClick={function () { setRoleF(roleF === r.key ? "" : r.key); }}>{r.label} {n}</button>;
          })}
          <select value={clubF} onChange={function (e) { setClubF(e.target.value); }} aria-label="Filter op club" className="bh-chip" style={{ paddingRight: 8 }}>
            <option value="">Club: alle</option>
            {clubs.map(function (c) { return <option key={c.id} value={c.id}>{c.name || c.slug}</option>; })}
          </select>
        </div>
        <div style={{ display: "flex", gap: 20, flexWrap: "wrap", alignItems: "flex-start" }}>
          <div className="bh-card" style={{ flex: "999 1 520px", minWidth: 0, overflowX: "auto" }}>
            <table className="bh-table">
              <thead><tr><th>Naam</th><th>Club</th><th>Rol</th><th>Pakket</th></tr></thead>
              <tbody>
                {list.map(function (u) {
                  return (
                    <tr key={u.uid} className={sel === u.uid ? "sel" : ""} onClick={function () { pick(u); }} tabIndex={0} onKeyDown={function (e) { if (e.key === "Enter") pick(u); }} style={{ opacity: busy === u.uid ? 0.5 : 1 }}>
                      <td><div style={{ fontWeight: 700 }}>{u.name || "(geen naam)"}</div><div className="bh-sub">{u.email}</div></td>
                      <td>{trainerClubs[u.clubId] ? <span className="bh-sub" style={{ fontSize: 14 }}>Losse trainer</span> : clubName(u.clubId)}</td>
                      <td>{roleLabel(u.role)}</td>
                      <td>{pakketOf[u.uid] ? packageLabel(pakketOf[u.uid]) : <span className="bh-sub">–</span>}</td>
                    </tr>
                  );
                })}
                {users && list.length === 0 && <tr style={{ cursor: "default" }}><td colSpan={4} className="bh-sub" style={{ padding: 20 }}>Geen gebruikers gevonden.</td></tr>}
              </tbody>
            </table>
          </div>
          {selU && (
            <div ref={panelRef} style={{ flex: "1 1 320px", maxWidth: 400, minWidth: 0, display: "flex" }}>
              <UserPanel u={selU} me={profile} clubName={trainerClubs[selU.clubId] ? "Losse trainer" : clubName(selU.clubId)} pakket={pakketOf[selU.uid]} busy={busy === selU.uid}
                onClose={function () { setSel(null); }} onRole={changeRole} onName={saveName} onLink={newLink}
                onDelete={function (u) { setMdl({ type: "delete", user: u }); }} />
            </div>
          )}
        </div>
      </>
    );
  }

  function renderClubs() {
    var rows = clubs.map(function (c) {
      var members = (users || []).filter(function (u) { return u.clubId === c.id; });
      var head = members.filter(function (u) { return roleRank(u.role) >= 1; }).sort(function (a, b) { return roleRank(a.role) - roleRank(b.role); })[0];
      return { c: c, n: members.length, head: head, trainer: !!trainerClubs[c.id] };
    }).sort(function (a, b) { return b.n - a.n; });
    return (
      <>
        <Header title="Clubs" sub={clubs.length + (clubs.length === 1 ? " omgeving" : " omgevingen") + ", inclusief losse trainers"} />
        <section className="bh-card" style={{ overflowX: "auto" }}>
          <table className="bh-table">
            <thead><tr><th>Club</th><th>Soort</th><th>Hoofdtrainer</th><th>Accounts</th><th></th></tr></thead>
            <tbody>
              {rows.map(function (r) {
                return (
                  <tr key={r.c.id} style={{ cursor: "default" }}>
                    <td><div style={{ fontWeight: 700 }}>{r.c.name || r.c.slug}</div><div className="bh-sub">/{r.c.slug}</div></td>
                    <td>{r.trainer ? "Losse trainer" : "Club"}</td>
                    <td>{r.head ? (r.head.name || r.head.email) : <span className="bh-sub">–</span>}</td>
                    <td>{r.n}</td>
                    <td style={{ textAlign: "right" }}><button type="button" className="bh-btn2" style={{ minHeight: 36 }} onClick={function () { navigate("/" + r.c.slug); }}><Icon d={IC.open} size={14} />Openen</button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </section>
      </>
    );
  }

  function renderLicenties() {
    return (
      <>
        <Header title="Licenties & facturen" />
        <section className="bh-card" style={{ padding: 28, maxWidth: 640 }}>
          <h2 style={{ fontSize: 18, fontWeight: 800, marginBottom: 8 }}>Komt met het licentiemodel</h2>
          <p style={{ fontSize: 15, lineHeight: 1.6, color: "#4A4741" }}>Hier zie je straks per seizoen welke licenties actief zijn, wanneer ze verlopen en of de factuur is betaald. Tot die tijd staat het gekozen pakket bij de aanmelding en bij de gebruiker.</p>
        </section>
      </>
    );
  }

  function renderModal() {
    if (!mdl) return null;
    var close = function () { setMdl(null); };
    if (mdl.type === "reject") return (
      <Modal onClose={close}>
        <h3 style={{ fontSize: 18, color: "#B83232", marginBottom: 10, fontWeight: 800 }}>Aanmelding afwijzen?</h3>
        <p style={{ fontSize: 14, color: "#4A4741", marginBottom: 16, lineHeight: 1.6 }}>Het account van {mdl.req.name} ({mdl.req.email}) wordt verwijderd en {String(mdl.req.name || "").split(" ")[0]} krijgt een mail dat de aanmelding niet is goedgekeurd.</p>
        <div style={{ display: "flex", gap: 8 }}>
          <button type="button" className="bh-btn2" style={{ flex: 1 }} onClick={close}>Annuleren</button>
          <button type="button" className="bh-btn" style={{ flex: 1, background: "#B83232" }} onClick={function () { reject(mdl.req); }}>Afwijzen</button>
        </div>
      </Modal>
    );
    if (mdl.type === "delete") return (
      <Modal onClose={close}>
        <h3 style={{ fontSize: 18, color: "#B83232", marginBottom: 10, fontWeight: 800 }}>Account verwijderen?</h3>
        <p style={{ fontSize: 14, color: "#4A4741", marginBottom: 16, lineHeight: 1.6 }}>{mdl.user.name || mdl.user.email} kan daarna niet meer inloggen. Dit kun je niet ongedaan maken.</p>
        <div style={{ display: "flex", gap: 8 }}>
          <button type="button" className="bh-btn2" style={{ flex: 1 }} onClick={close}>Annuleren</button>
          <button type="button" className="bh-btn" style={{ flex: 1, background: "#B83232" }} onClick={function () { doDelete(mdl.user); }}>Verwijderen</button>
        </div>
      </Modal>
    );
    if (mdl.type === "link") return (
      <Modal onClose={close}>
        <h3 style={{ fontSize: 18, marginBottom: 10, fontWeight: 800 }}>{mdl.created ? "Account aangemaakt ✓" : "Nieuwe wachtwoordlink"}</h3>
        <p style={{ fontSize: 14, color: "#4A4741", marginBottom: 12, lineHeight: 1.6 }}>
          {mdl.mailSent ? <span>Er is een mail gestuurd naar <b>{mdl.user.email}</b> met een link om een wachtwoord in te stellen.</span>
            : <span>De mail naar <b>{mdl.user.email}</b> kon niet verstuurd worden.</span>}
          {" "}Je kunt de link hieronder ook zelf doorsturen. De link is <b>1 uur</b> geldig.
        </p>
        <LinkBox link={mdl.link} />
      </Modal>
    );
    if (mdl.type === "create") return <CreateModal clubs={clubs} onClose={close} onCreated={function (u, link, mailSent) { setMdl({ type: "link", created: true, user: u, link: link, mailSent: mailSent }); load(); }} resetPassword={resetPassword} />;
    return null;
  }

  var NAV_ICON = { overzicht: "beheer", aanmeldingen: "aanmeldingen", gebruikers: "accounts", clubs: "clubs", licenties: "licenties" };
  var navItems = VIEWS.map(function (v) {
    return { key: v.key, label: v.label, icon: NAV_ICON[v.key], active: view === v.key, badge: v.key === "aanmeldingen" && pending.length ? pending.length : null,
      onClick: function () { setView(v.key); setErr(""); window.scrollTo(0, 0); } };
  });
  var bottomItems = [{ key: "terug", label: "Terug naar de app", icon: "terug", onClick: function () { navigate("/"); } }];

  return (
    <div className="bh">
      <style>{CSS}</style>
      {wide && <SideNav subtitle="Beheer" onBrand={function () { setView("overzicht"); }} items={navItems} bottomItems={bottomItems} collapsed={collapsed} onToggle={toggleCollapsed} />}
      <div style={{ flex: 1, minWidth: 0 }}>
      {!wide && <MobileBar subtitle="Beheer" onBrand={function () { setView("overzicht"); }} items={navItems} bottomItems={bottomItems} />}
      <main className="bh-main">
        {loadErr && <p style={{ color: "#B83232", fontSize: 14, marginBottom: 16 }}>{loadErr}</p>}
        {err && <p role="alert" style={{ color: "#B83232", fontSize: 14, marginBottom: 16, background: "#FBEDEC", padding: "10px 14px", borderRadius: 10 }}>{err}</p>}
        {view === "overzicht" && renderOverzicht()}
        {view === "aanmeldingen" && renderAanmeldingen()}
        {view === "gebruikers" && renderGebruikers()}
        {view === "clubs" && renderClubs()}
        {view === "licenties" && renderLicenties()}
      </main>
      </div>
      {renderModal()}
    </div>
  );
}

function CreateModal({ clubs, onClose, onCreated, resetPassword }) {
  var [name, setName] = useState("");
  var [email, setEmail] = useState("");
  var [clubId, setClubId] = useState(clubs[0] ? clubs[0].id : "");
  var [role, setRole] = useState("trainer");
  var [busy, setBusy] = useState(false);
  var [err, setErr] = useState("");
  async function submit(e) {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      var res = await fnCreate({ email: email.trim(), name: name.trim(), role: role, clubId: clubId });
      var mailSent = true;
      try { await resetPassword(email.trim()); } catch (e2) { console.error(e2); mailSent = false; }
      onCreated({ name: name.trim(), email: email.trim() }, res.data.link, mailSent);
    } catch (e2) {
      console.error(e2);
      setErr(errText(e2));
      setBusy(false);
    }
  }
  return (
    <Modal onClose={onClose}>
      <form onSubmit={submit}>
        <h3 style={{ fontSize: 18, marginBottom: 14, fontWeight: 800 }}>Account aanmaken</h3>
        <label style={{ display: "block", marginBottom: 10 }}><span className="bh-lbl">Naam</span><input className="bh-inp" value={name} onChange={function (e) { setName(e.target.value); }} required autoFocus /></label>
        <label style={{ display: "block", marginBottom: 10 }}><span className="bh-lbl">E-mailadres</span><input className="bh-inp" type="email" value={email} onChange={function (e) { setEmail(e.target.value); }} required /></label>
        <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
          <label style={{ flex: 1 }}><span className="bh-lbl">Club</span>
            <select className="bh-inp" value={clubId} onChange={function (e) { setClubId(e.target.value); }}>
              {clubs.map(function (c) { return <option key={c.id} value={c.id}>{c.name || c.slug}</option>; })}
            </select>
          </label>
          <label style={{ flex: 1 }}><span className="bh-lbl">Rol</span>
            <select className="bh-inp" value={role} onChange={function (e) { setRole(e.target.value); }}>
              {ROLES.map(function (r) { return <option key={r.key} value={r.key}>{r.label}</option>; })}
            </select>
          </label>
        </div>
        {err && <p style={{ color: "#B83232", fontSize: 13, marginBottom: 10 }}>{err}</p>}
        <div style={{ display: "flex", gap: 8 }}>
          <button type="button" className="bh-btn2" style={{ flex: 1 }} onClick={onClose}>Annuleren</button>
          <button type="submit" className="bh-btn" style={{ flex: 1 }} disabled={busy}>{busy ? "Bezig..." : "Aanmaken"}</button>
        </div>
      </form>
    </Modal>
  );
}
